import React, { useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Package, Cpu, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const RequestCatalogDialog = lazy(() => import('./RequestCatalogDialog'));

const RequestCatalogSection: React.FC = () => {
  const { user, authReady } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const signedIn = authReady && !!user;

  return (
    <section
      className="relative z-20 py-10 sm:py-16 px-4 sm:px-6"
      aria-labelledby="request-catalog-heading"
    >
      <div className="container mx-auto max-w-3xl">
        <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/15 p-6 sm:p-8 text-center">
          <div className="flex justify-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-full bg-brand-blue/15 border border-brand-blue/40 flex items-center justify-center">
              <Package className="w-5 h-5 text-brand-blue" />
            </div>
            <div className="w-10 h-10 rounded-full bg-brand-magenta/15 border border-brand-magenta/40 flex items-center justify-center">
              <Cpu className="w-5 h-5 text-brand-magenta" />
            </div>
          </div>
          <h2
            id="request-catalog-heading"
            className="text-xl sm:text-2xl font-semibold text-white drop-shadow-sm mb-2"
          >
            Missing a panel or processor?
          </h2>
          <p className="text-sm sm:text-base text-white/70 max-w-lg mx-auto mb-5">
            Request a panel or processor and we'll add it to the catalog. Include the
            manufacturer, model, key specs, and a photo of the spec sheet if you have one.
          </p>
          {signedIn ? (
            <Button
              size="lg"
              onClick={() => setOpen(true)}
              className="bg-gradient-to-r from-brand-blue to-brand-magenta text-white w-full sm:w-auto h-auto py-3 px-4 sm:px-6 whitespace-normal text-sm sm:text-base leading-tight"
            >
              <span>Request a panel or processor</span>
              <ArrowRight className="ml-2 w-4 h-4 shrink-0" />
            </Button>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/auth')}
                className="bg-white/5 text-white border-white/30 hover:bg-white/10 w-full sm:w-auto h-auto py-3 px-4 sm:px-6 whitespace-normal text-sm sm:text-base leading-tight"
              >
                Sign in to submit a request
              </Button>
            </div>
          )}
        </div>
      </div>

      {open && (
        <Suspense fallback={null}>
          <RequestCatalogDialog open={open} onOpenChange={setOpen} />
        </Suspense>
      )}
    </section>
  );
};

export default RequestCatalogSection;
